import { useEffect, useId, useRef, useState } from "react";
import type React from "react";
import type { LucideIcon } from "lucide-react";
import {
  Activity,
  Building2,
  HeartPulse,
  LoaderCircle,
  Search,
  Stethoscope,
  X,
} from "lucide-react";
import type { UseMutationResult } from "@tanstack/react-query";

import { Badge } from "@/components/ui/badge";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Tabs, TabsContent, TabsList, TabsTrigger } from "@/components/ui/tabs";
import { cn } from "@/lib/utils";
import { useDismissOnOutside } from "@/hooks/use-dismiss-on-outside";
import type {
  HcbbSuggestion,
  HcbbSuggestionGroups,
  HcbbSuggestionKind,
} from "@/model/hcbb-cost-estimator";

type TabConfig = {
  kind: HcbbSuggestionKind;
  label: string;
  icon: LucideIcon;
  badge: string;
};

/** Tab order is fixed so the dropdown never reshuffles between keystrokes. */
const TABS: TabConfig[] = [
  {
    kind: "procedure",
    label: "Procedures",
    icon: Activity,
    badge: "Procedure",
  },
  { kind: "doctor", label: "Doctors", icon: Stethoscope, badge: "Doctor" },
  { kind: "facility", label: "Facilities", icon: Building2, badge: "Facility" },
  {
    kind: "specialty",
    label: "Specialties",
    icon: HeartPulse,
    badge: "Specialty",
  },
];

const EMPTY_GROUPS: HcbbSuggestionGroups = {
  procedure: [],
  doctor: [],
  facility: [],
  specialty: [],
};

const normalize = (value: string) =>
  value.toLowerCase().replace(/[^a-z0-9]/g, "");

const byLabel = (a: HcbbSuggestion, b: HcbbSuggestion) =>
  a.label.localeCompare(b.label, undefined, {
    numeric: true,
    sensitivity: "base",
  });

/**
 * A row is a match when its label starts with what was typed — an exact label
 * being the shortest case of that. The endpoint has already matched every row
 * against the term, so merely containing it says nothing; carrying the typed
 * prefix does.
 */
const isMatchFor = (item: HcbbSuggestion, normalizedQuery: string) =>
  normalize(item.label).startsWith(normalizedQuery);

/**
 * Splits the list into every row that matches what was typed and the rest,
 * both halves alphabetical. All the matches lead, so a query that narrows to
 * several procedures — "brain mri" over the three contrast variants — reads
 * them together at the top instead of one promoted and the others buried down
 * the alphabet.
 */
const orderByMatch = (items: HcbbSuggestion[], query: string) => {
  const normalizedQuery = normalize(query);
  if (!normalizedQuery) {
    return { orderedItems: [...items].sort(byLabel), matchCount: 0 };
  }

  const matches: HcbbSuggestion[] = [];
  const rest: HcbbSuggestion[] = [];

  for (const item of items) {
    (isMatchFor(item, normalizedQuery) ? matches : rest).push(item);
  }

  return {
    orderedItems: [...matches.sort(byLabel), ...rest.sort(byLabel)],
    matchCount: matches.length,
  };
};

type HcbbSearchAutocompleteProps = {
  label?: string;
  placeholder?: string;
  placeholderIcon?: LucideIcon;
  value?: string;
  queryFunction: UseMutationResult<
    HcbbSuggestionGroups,
    Error,
    { queryString: string }
  >;
  onSelect: (suggestion: HcbbSuggestion) => void;
  onClear?: () => void;
  minSearchLength?: number;
  debounceTime?: number;
  /**
   * Caps how many rows each tab renders; omit for the full group. A tab over
   * the cap gets a "View all" toggle so every result stays reachable.
   */
  resultLimit?: number;
};

/**
 * Free-text search box whose dropdown splits results into Procedures, Doctors,
 * Facilities and Specialties. Purpose-built for the enhanced cost estimator:
 * the shared `search-autocomplete` is a flat, single-entity list used by five
 * other screens, so it is deliberately left untouched.
 */
export const HcbbSearchAutocomplete = ({
  label,
  placeholder = "Procedure | ",
  placeholderIcon: PlaceholderIcon = Search,
  value,
  queryFunction,
  onSelect,
  onClear,
  minSearchLength = 3,
  debounceTime = 300,
  resultLimit,
}: HcbbSearchAutocompleteProps) => {
  const [searchString, setSearchString] = useState(value ?? "");
  const [groups, setGroups] = useState<HcbbSuggestionGroups>(EMPTY_GROUPS);
  const [isOpen, setIsOpen] = useState(false);
  const [activeTab, setActiveTab] = useState<HcbbSuggestionKind>("procedure");

  const containerRef = useRef<HTMLDivElement>(null);
  const debounceRef = useRef<NodeJS.Timeout | null>(null);
  const inputId = useId();

  const { isPending, mutateAsync } = queryFunction;
  const totalResults = TABS.reduce(
    (total, tab) => total + groups[tab.kind].length,
    0,
  );

  useEffect(() => {
    setSearchString(value ?? "");
  }, [value]);

  useEffect(() => {
    return () => {
      if (debounceRef.current) clearTimeout(debounceRef.current);
    };
  }, []);

  // Escape is handled on the input itself, where focus can also be restored,
  // so only the outside-click half is taken from the shared hook.
  useDismissOnOutside(containerRef, isOpen, (reason) => {
    if (reason === "outside") setIsOpen(false);
  });

  const runSearch = async (queryString: string) => {
    // A failed lookup reads to the member exactly as a term nothing matched:
    // the dropdown still opens on an empty set and says "No results found".
    // Surfacing the failure instead would put an error in front of someone who
    // only mistyped, and the next keystroke retries either way.
    const result = await mutateAsync({ queryString }).catch(() => EMPTY_GROUPS);

    setGroups(result);
    // Land on the first tab that actually has hits.
    setActiveTab(
      TABS.find((tab) => result[tab.kind].length > 0)?.kind ?? "procedure",
    );
    setIsOpen(true);
  };

  const handleInputChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    const nextValue = event.target.value;
    setSearchString(nextValue);

    if (debounceRef.current) clearTimeout(debounceRef.current);

    // Trimmed for the length check and the query itself, not for the input's
    // own value: "w  " is one meaningful character plus two keystrokes still
    // in flight toward a second word, not three characters worth searching.
    const trimmedValue = nextValue.trim();
    if (trimmedValue.length < minSearchLength) {
      setGroups(EMPTY_GROUPS);
      setIsOpen(false);
      return;
    }

    debounceRef.current = setTimeout(() => {
      void runSearch(trimmedValue);
    }, debounceTime);
  };

  const handleClear = () => {
    if (debounceRef.current) clearTimeout(debounceRef.current);
    setSearchString("");
    setGroups(EMPTY_GROUPS);
    setIsOpen(false);
    onClear?.();
  };

  const handleSelect = (suggestion: HcbbSuggestion) => {
    setIsOpen(false);
    onSelect(suggestion);
  };

  const handleKeyDown = (event: React.KeyboardEvent) => {
    if (event.key === "Escape" && isOpen) {
      event.stopPropagation();
      setIsOpen(false);
    }
  };

  const hasQuery = searchString.trim().length >= minSearchLength;

  return (
    <div className="space-y-2" ref={containerRef} onKeyDown={handleKeyDown}>
      {label && (
        <Label htmlFor={inputId} className="text-sm font-semibold">
          {label}
        </Label>
      )}

      <div className="relative">
        {/*
          Deliberately not role="combobox": the results live inside tab panels,
          so there is no single listbox to point aria-controls/activedescendant
          at. A plain input plus the live region below and real <button> rows
          announces correctly instead of half-implementing the combobox pattern.
        */}
        {/* Without a visible label the placeholder is the only description of
            the field, so it doubles as the name rather than leaving it blank. */}
        <Input
          id={inputId}
          type="text"
          aria-label={label ? undefined : placeholder}
          value={searchString}
          onChange={handleInputChange}
          onFocus={() => {
            if (hasQuery && totalResults > 0) setIsOpen(true);
          }}
          placeholder={placeholder}
          className="pr-10 text-sm"
        />

        <div className="absolute inset-y-0 right-0 flex items-center pr-3">
          {isPending ? (
            <LoaderCircle className="size-4 animate-spin text-muted-foreground" />
          ) : searchString ? (
            <button
              type="button"
              onClick={handleClear}
              aria-label="Clear search"
              className="rounded-sm text-muted-foreground hover:text-foreground focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring"
            >
              <X className="size-4" />
            </button>
          ) : (
            <PlaceholderIcon
              aria-hidden="true"
              className="size-4 text-muted-foreground"
            />
          )}
        </div>

        <span aria-live="polite" className="sr-only">
          {isOpen ? `${totalResults} results found` : ""}
        </span>

        {isOpen && (
          // Sized to its content, not to the input: four tab labels and a
          // procedure name do not fit a third of the form row, and the
          // dropdown scrolling sideways hides both. Grows rightward from the
          // input's left edge, capped so it stays on screen.
          <Card className="absolute left-0 z-20 mt-2 w-max min-w-full max-w-[min(40rem,90vw)] overflow-hidden shadow-lg">
            <CardContent className="p-0">
              {totalResults === 0 ? (
                <p className="p-4 text-sm text-muted-foreground">
                  No results found
                </p>
              ) : (
                <Tabs
                  value={activeTab}
                  onValueChange={(next) =>
                    setActiveTab(next as HcbbSuggestionKind)
                  }
                >
                  {/* Underline tabs — overrides the shared pill styling. */}
                  <TabsList className="h-auto w-full flex-wrap justify-start gap-2 rounded-none border-b bg-transparent p-0 px-2">
                    {TABS.map((tab) => (
                      <TabsTrigger
                        key={tab.kind}
                        value={tab.kind}
                        disabled={groups[tab.kind].length === 0}
                        className="rounded-none border-b-2 border-transparent p-3 text-sm font-medium text-muted-foreground shadow-none data-[state=active]:border-primary data-[state=active]:bg-transparent data-[state=active]:text-primary data-[state=active]:shadow-none"
                      >
                        {tab.label} ({groups[tab.kind].length})
                      </TabsTrigger>
                    ))}
                  </TabsList>

                  {TABS.map((tab) => (
                    <TabsContent
                      key={tab.kind}
                      value={tab.kind}
                      className="mt-0"
                    >
                      <SuggestionList
                        tab={tab}
                        items={groups[tab.kind]}
                        query={searchString}
                        onSelect={handleSelect}
                        resultLimit={resultLimit}
                      />
                    </TabsContent>
                  ))}
                </Tabs>
              )}
            </CardContent>
          </Card>
        )}
      </div>
    </div>
  );
};

const SuggestionList = ({
  tab,
  items,
  query,
  onSelect,
  resultLimit,
}: {
  tab: TabConfig;
  items: HcbbSuggestion[];
  query: string;
  onSelect: (suggestion: HcbbSuggestion) => void;
  resultLimit?: number;
}) => {
  const listId = useId();
  const [isExpanded, setIsExpanded] = useState(false);

  // A fresh set of items means a new search (or the endpoint answered again
  // for the same term); either way the old expansion no longer applies to
  // what's on screen, so each new result set starts capped again.
  useEffect(() => {
    setIsExpanded(false);
  }, [items]);

  const { orderedItems, matchCount } = orderByMatch(items, query);
  const Icon = tab.icon;

  if (items.length === 0) {
    return (
      <p className="p-4 text-sm text-muted-foreground">
        No {tab.label.toLowerCase()} found
      </p>
    );
  }

  const isTruncatable =
    resultLimit !== undefined && orderedItems.length > resultLimit;
  // orderByMatch has already hoisted every match to the front, so capping
  // still shows the best matches rather than an arbitrary prefix.
  const visibleItems =
    isTruncatable && !isExpanded
      ? orderedItems.slice(0, resultLimit)
      : orderedItems;

  return (
    <>
      <ul id={listId} className="max-h-[320px] overflow-y-auto">
        {visibleItems.map((item, index) => {
          // Matches are still the leading items of visibleItems, so position
          // alone tells a match from the rest.
          const isMatch = index < matchCount;

          return (
            <li key={item.id}>
              {/* A match is called out by the badge, the icon and the weight —
                  not by its background. Every row shades on hover alone, so the
                  shaded row is always the one under the pointer. */}
              <button
                type="button"
                onClick={() => onSelect(item)}
                className="flex w-full items-center gap-3 px-4 py-3 text-left transition-colors hover:bg-muted focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
              >
                <Icon aria-hidden="true" className={cn("size-4 shrink-0")} />
                <span className={cn("flex-1 truncate text-sm")}>
                  {item.label}
                </span>
                {isMatch && (
                  <Badge className="shrink-0 text-[10px] uppercase tracking-wide">
                    Matches search
                  </Badge>
                )}
              </button>
            </li>
          );
        })}
      </ul>

      {isTruncatable && (
        // Same button toggles both directions rather than being replaced by a
        // separate "show fewer" control — swapping in a new element here would
        // unmount the one a keyboard user just activated and drop focus to
        // <body> (see wcag-aa-compliance: focus after content changes).
        <button
          type="button"
          aria-expanded={isExpanded}
          aria-controls={listId}
          onClick={() => setIsExpanded((expanded) => !expanded)}
          className="w-full border-t px-4 py-2 text-center text-sm font-medium text-primary hover:underline focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-inset focus-visible:ring-ring"
        >
          {isExpanded
            ? "Show fewer"
            : `View all ${orderedItems.length} ${tab.label.toLowerCase()}`}
        </button>
      )}
    </>
  );
};
